import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import CommissionService, {CommissionRestrictionStatus} from '../api/CommissionService';
import {UserTypes} from '../constants/UserTypes';
import {getAuthenticatedUserType, isTokenValid} from '../utils/Utils';
import Cookies from 'js-cookie';
import {AUTH_TOKEN} from '../constants/Constants';

interface SuspensionContextValue {
    /** Raw restriction status from the backend. Null while loading or for non-RCIC users. */
    restrictionStatus: CommissionRestrictionStatus | null;
    /** True while the initial restriction-status fetch is in progress. */
    isLoading: boolean;
    /** True when the account is suspended (tier 4 — 30+ days overdue). */
    isSuspended: boolean;
    /** True when new consultation requests are blocked (tier 3 — 14+ days overdue). */
    isBookingRestricted: boolean;
    /**
     * True whenever the RCIC has an issued, unpaid commission invoice — including one
     * that is not yet due. This drives the dashboard reminder banner, which must appear
     * from the day the invoice is issued rather than only once it is overdue.
     */
    hasOverdueWarning: boolean;
    /** Re-fetch restriction status (call after payment confirmation). */
    refresh: () => Promise<void>;
}

const defaultValue: SuspensionContextValue = {
    restrictionStatus: null,
    isLoading: false,
    isSuspended: false,
    isBookingRestricted: false,
    hasOverdueWarning: false,
    refresh: async () => {},
};

const SuspensionContext = createContext<SuspensionContextValue>(defaultValue);

export const useSuspension = (): SuspensionContextValue => useContext(SuspensionContext);

/**
 * SuspensionProvider
 *
 * Wraps the application and provides real-time invoice restriction/suspension
 * status for authenticated RCIC users.  Non-RCIC users receive the default
 * (unrestricted) values without making any API calls.
 */
export const SuspensionProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const [restrictionStatus, setRestrictionStatus] = useState<CommissionRestrictionStatus | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const refresh = useCallback(async () => {
        const tokenValid = isTokenValid();
        const userType = getAuthenticatedUserType();
        if (!tokenValid) return;
        if (userType !== UserTypes.RCIC) return;

        setIsLoading(true);
        try {
            const status = await CommissionService.getRestrictionStatus();
            setRestrictionStatus(status);
        } catch (e) {
            console.error('[Suspension] API error:', e);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Check current authentication state to trigger effect when token or user type changes
    const currentToken = Cookies.get(AUTH_TOKEN);
    const currentUserType = getAuthenticatedUserType();

    useEffect(() => {
        void refresh();
    }, [refresh, currentToken, currentUserType]);

    const isSuspended = restrictionStatus?.isRestricted ?? false;
    const isBookingRestricted = (restrictionStatus?.bookingRestricted ?? false) || isSuspended;

    // Any issued, unpaid invoice warrants the banner. `hasUnpaidInvoice` is the
    // authoritative signal; the remaining checks keep the banner working against
    // older backends that do not yet return it.
    const hasOverdueWarning = (restrictionStatus?.hasUnpaidInvoice ?? false)
        || (restrictionStatus?.hasWarning ?? false)
        || isSuspended
        || (restrictionStatus?.daysOverdue ?? 0) > 0;

    return (
        <SuspensionContext.Provider
            value={{restrictionStatus, isLoading, isSuspended, isBookingRestricted, hasOverdueWarning, refresh}}>
            {children}
        </SuspensionContext.Provider>
    );
};
